import {
  EvidenceProfileSchema,
  SourceCoverageSchema,
} from '../../../../contracts/evidence/evidence-profile';
import { z } from 'zod';
import { MAX_CAPTURE_OCCURRENCES } from '../../../../contracts/evidence/finding-capture';
// One definition of the digest shape, shared with the upload boundary that
// parses it back off the header.
import { FindingDigestSchema } from '../../../../contracts/impact/repo-scan';
import {
  RelationalFindingsSchema,
  ToolRunStatusSchema,
} from './static-analysis';

// --- Primitive schemas ---

export const MetaSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean()]),
);

// --- Graph schemas ---

export const GraphNodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  meta: MetaSchema,
  createdAt: z.string(),
});

export const GraphEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  type: z.string(),
  category: z.string(),
  meta: MetaSchema,
  createdAt: z.string(),
});

export const SerializedGraphSchema = z.object({
  nodes: z.array(GraphNodeSchema),
  edges: z.array(GraphEdgeSchema),
  meta: MetaSchema,
});

// --- Analysis schemas ---

export const StalestSourceSchema = z.object({
  description: z.string(),
  verified: z.string(),
});

export const GapEntrySchema = z.object({
  nodeId: z.string(),
  groundedness: z.number(),
  downstreamCount: z.number(),
  gapScore: z.number(),
  stalestSource: StalestSourceSchema.optional(),
});

export const ImpactEntrySchema = z.object({
  nodeId: z.string(),
  score: z.number(),
  distance: z.number(),
});

export const ConflictingPathPairSchema = z.object({
  positive: z.array(z.string()),
  negative: z.array(z.string()),
});

export const ContradictionEntrySchema = z.object({
  nodeA: z.string(),
  nodeB: z.string(),
  directEdge: GraphEdgeSchema.optional(),
  conflictingPaths: z.array(ConflictingPathPairSchema),
  severity: z.number().optional(),
});

export const CycleBreakerSchema = z.object({
  edge: GraphEdgeSchema,
  cyclesRemoved: z.number(),
});

export const CycleEntrySchema = z.object({
  nodes: z.array(z.string()),
  edges: z.array(GraphEdgeSchema),
  score: z.number(),
});

export const CycleAnalysisResultSchema = z.object({
  cycles: z.array(CycleEntrySchema),
  breakerEdges: z.array(CycleBreakerSchema),
  totalCycleCount: z.number(),
});

// --- Sensitivity schemas ---

export const SensitivityDimensionSchema = z.object({
  name: z.string(),
  weight: z.number(),
});

export const SensitivityEntrySchema = z.object({
  nodeId: z.string(),
  uplift: z.number(),
  downstreamCount: z.number(),
  groundedness: z.number(),
});

export const SensitivityResultSchema = z.object({
  dimensions: z.array(SensitivityDimensionSchema),
  entries: z.array(SensitivityEntrySchema),
});

// --- Refactor priority schemas ---

export const RefactorDimensionScoresSchema = z.object({
  cycleImpact: z.number(),
  blastRadius: z.number(),
  risk: z.number(),
  feasibility: z.number(),
  edgeStrength: z.number(),
});

export const RefactorActionKindSchema = z.enum([
  'break_dependency',
  'extract_shared_types',
  'split_module',
  'increase_coverage',
  'reduce_bus_factor',
]);

export const TargetEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  type: z.string(),
});

export const RefactorActionSchema = z.object({
  kind: RefactorActionKindSchema,
  targetNodeId: z.string(),
  targetEdge: TargetEdgeSchema.optional(),
  priority: z.number(),
  dimensions: RefactorDimensionScoresSchema,
  rationale: z.string(),
  estimatedHours: z.number().optional(),
});

export const RefactorConfidenceSchema = z.object({
  level: z.enum(['high', 'medium', 'low']),
  dominanceGap: z.number(),
  rationale: z.string(),
});

export const RefactorPriorityResultSchema = z.object({
  actions: z.array(RefactorActionSchema),
  weights: RefactorDimensionScoresSchema,
  confidence: RefactorConfidenceSchema.optional(),
});

// --- Health recommendation schemas ---

export const HealthDimensionScoresSchema = z.object({
  churnScore: z.number(),
  couplingScore: z.number(),
  coverageGap: z.number(),
  sizeScore: z.number(),
  busFactor: z.number(),
});

export const HealthRecommendationSchema = z.object({
  kind: z.enum([
    'reduce_coupling',
    'add_tests_hotspot',
    'split_large_file',
    'reduce_churn_risk',
    'stabilize_api_surface',
    'reduce_complexity',
    'address_security_findings',
    'stabilize_production',
    'remove_dead_code',
    'review_unreviewed_change',
  ]),
  targetNodeId: z.string(),
  priority: z.number(),
  dimensions: HealthDimensionScoresSchema,
  rationale: z.string(),
});

export const HealthRecommendationResultSchema = z.object({
  recommendations: z.array(HealthRecommendationSchema),
  saMeasured: z.boolean().optional(),
});

// --- Hotspot schemas ---

export const HotspotDimensionsSchema = z.object({
  couplingAmplifier: z.number(),
  churnAmplifier: z.number(),
  coverageGap: z.number(),
  busFactorAmplifier: z.number(),
  saIssueAmplifier: z.number(),
  complexityAmplifier: z.number(),
  incidentAmplifier: z.number(),
  deadCodePenalty: z.number(),
  saMeasured: z.boolean().optional(),
});

export const HotspotSchema = z.object({
  nodeId: z.string(),
  score: z.number(),
  fanIn: z.number(),
  fanOut: z.number(),
  churn: z.number(),
  testCoverage: z.number(),
  busFactor: z.number().optional(),
  dimensions: HotspotDimensionsSchema.optional(),
  rationale: z.string(),
});

// --- Dead-code schemas ---

export const DeadCodeConfidenceSchema = z.enum(['high', 'medium', 'low']);

export const DeadCodeConfidenceTierSchema = z.enum([
  'cross-confirmed',
  'graph-only',
  'sa-only',
]);

export const DeadCodeEntrySchema = z.object({
  nodeId: z.string(),
  name: z.string(),
  path: z.string(),
  nodeType: z.string(),
  symbolType: z.string().optional(),
  confidence: DeadCodeConfidenceSchema,
  confidenceTier: DeadCodeConfidenceTierSchema.optional(),
  saDeadCodeCount: z.number().optional(),
  rationale: z.string(),
});

export const DeadCodeSummarySchema = z.object({
  totalDead: z.number(),
  deadModuleCount: z.number(),
  deadFileCount: z.number(),
  deadSymbolCount: z.number(),
  entryPointCount: z.number(),
  reachabilityPercentage: z.number(),
});

export const DeadCodeResultSchema = z.object({
  deadSymbols: z.array(DeadCodeEntrySchema),
  deadFiles: z.array(DeadCodeEntrySchema),
  deadModules: z.array(DeadCodeEntrySchema),
  suspiciousFiles: z.array(DeadCodeEntrySchema),
  summary: DeadCodeSummarySchema,
});

// --- What-If schemas ---

export const WhatIfActionKindSchema = RefactorActionKindSchema;

export const WhatIfActionSchema = z.object({
  kind: WhatIfActionKindSchema,
  targetNodeId: z.string(),
  targetEdge: TargetEdgeSchema.optional(),
});

export const WhatIfMetricsSchema = z.object({
  nodeCount: z.number(),
  edgeCount: z.number(),
  totalCycles: z.number(),
  totalHotspotScore: z.number(),
  avgBlastRadius: z.number(),
  maxBlastRadius: z.number(),
  refactorActionCount: z.number(),
  healthRecommendationCount: z.number(),
});

export const WhatIfResultSchema = z.object({
  action: WhatIfActionSchema,
  before: WhatIfMetricsSchema,
  after: WhatIfMetricsSchema,
  delta: WhatIfMetricsSchema,
});

/**
 * Counts describing a scan's review attribution, stored beside the graph.
 *
 * The per-node detail rides on each node's `evidence.sources`; this is the
 * scan-wide statement of what was possible, which a reader needs before
 * trusting or discounting the per-node figures.
 */
export const ReviewAttributionSummarySchema = z.object({
  version: z.number().int().positive(),
  /** Nodes the clone could attribute at least one pull request to. */
  nodesAttributed: z.number().int().nonnegative(),
  /** Of those, how many joined to GitHub review data. */
  nodesJoined: z.number().int().nonnegative(),
  clonePullRequests: z.number().int().nonnegative(),
  mirroredPullRequests: z.number().int().nonnegative(),
  /** PRE-cap count of joined nodes; `nodes` is a capped view of them. */
  total: z.number().int().nonnegative(),
  /** Optional so graphs written before the aggregate existed remain readable. */
  nodesWithUnreviewedMerges: z.number().int().nonnegative().optional(),
  nodes: z.array(
    z.object({
      nodeId: z.string(),
      prCount: z.number().int().nonnegative(),
      matchedCount: z.number().int().nonnegative(),
      reviewedCount: z.number().int().nonnegative(),
      unreviewedCount: z.number().int().nonnegative(),
      mergedUnreviewedCount: z.number().int().nonnegative(),
      reviewers: z.array(z.string()),
      medianHoursToMerge: z.number().nonnegative().nullable(),
    }),
  ),
});

// --- Top-level schemas ---

export const SerializedAnalysisSchema = z.object({
  groundednessScores: z.record(z.string(), z.number()),
  gapMap: z.array(GapEntrySchema),
  impactAnalyses: z.record(z.string(), z.array(z.string())),
  scoredImpactAnalyses: z.record(z.string(), z.array(ImpactEntrySchema)),
  contradictions: z.array(ContradictionEntrySchema),
  cycles: CycleAnalysisResultSchema.optional(),
  sensitivity: SensitivityResultSchema.optional(),
  refactorPriority: RefactorPriorityResultSchema.optional(),
  healthRecommendations: HealthRecommendationResultSchema.optional(),
  hotspots: z.array(HotspotSchema).optional(),
  deadCode: DeadCodeResultSchema.optional(),
  /**
   * What per-node review attribution the scan managed.
   *
   * `optional` because a stored blob outlives the code that wrote it: absent
   * means the scan predates this and MUST NOT be read as "measured, found
   * nothing". A summary present with `nodesJoined: 0` is that second claim,
   * and the two are acted on differently.
   */
  reviewAttribution: ReviewAttributionSummarySchema.optional(),
});

/** Per-module test-file presence, as measured from the checkout. */
export const ModuleCoverageSchema = z.object({
  moduleName: z.string(),
  testFileCount: z.number().int().nonnegative(),
  sourceFileCount: z.number().int().nonnegative(),
  testToSourceRatio: z.number(),
  testFunctionCount: z.number().int().nonnegative(),
});

/**
 * One finding as the analyser reported it, on its way to a durable capture.
 *
 * A pass-through of `NormalizedFinding`, validated at the blob boundary like
 * everything else this schema describes.
 */
export const CaptureFindingSchema = z.object({
  filePath: z.string(),
  severity: z.enum(['error', 'warning', 'info']),
  category: z.enum([
    'lint',
    'security',
    'complexity',
    'dead-code',
    'vulnerability',
    'best-practice',
    'type-safety',
  ]),
  ruleId: z.string(),
  tool: z.string(),
  line: z.number().optional(),
  column: z.number().optional(),
  message: z.string().optional(),
});

export const CachedScanSchema = z.object({
  serializedGraph: SerializedGraphSchema,
  analysis: SerializedAnalysisSchema,
  scannedAt: z.string(),
  githubRepo: z.string().optional(),
  saToolStatuses: z.array(ToolRunStatusSchema).optional(),
  /**
   * `scanTestCoverage` has always computed this; it simply never reached the
   * contract, so the health labels could not use it. Optional like
   * `saToolStatuses`: absent means the scan predates this, NOT that no module
   * has tests.
   */
  testCoverage: z.array(ModuleCoverageSchema).optional(),
  /** Which static-analysis suite ran; absent on scans predating the split. */
  saMode: z.enum(['all', 'parses-only']).optional(),
  /** Grouped/ranked results (#2596); prose in `static-analysis.ts`. */
  saRelationalFindings: z.array(RelationalFindingsSchema).optional(),
  /**
   * The bounded digest of individual findings (#2639).
   *
   * The full findings ride the blob this schema describes; this is the part
   * the `repo_scans` row also carries, so "what is new since last scan" costs
   * a row read rather than inflating an 8 MB predecessor.
   */
  saFindingDigest: FindingDigestSchema.optional(),
  /**
   * The analyser's own finding list, bounded but NOT collapsed.
   *
   * Carried beside `saFindingDigest` rather than derived from it. The digest
   * deduplicates by `(ruleId, path)` before it counts anything, so a rule that
   * fired eleven times in one file is one record there and the other ten are
   * unrecoverable from it. The durable capture is built from this list
   * instead, which is the only reason it can report multiplicity at all.
   */
  saCaptureFindings: z.array(CaptureFindingSchema).optional(),
  /**
   * How many findings the analyser produced, BEFORE `saCaptureFindings` was
   * bounded to fit this blob.
   *
   * Carried separately because the list above is a survivor set: counting it
   * downstream would describe a repository with 1,009 findings as completely
   * analysed with 500. Absent on scans predating this, which is unknown
   * rather than equal to the list's length.
   */
  saEvidenceProfile: EvidenceProfileSchema.optional(),
  saSourceCoverage: SourceCoverageSchema.optional(),
  saCaptureTotal: z.number().int().nonnegative().optional(),
  saCaptureLimit: z
    .number()
    .int()
    .positive()
    .max(MAX_CAPTURE_OCCURRENCES)
    .optional(),
  /**
   * The configuration the tools ACTUALLY read, as the producing scan observed
   * it. Carried rather than re-derived: only the scan process saw the
   * checkout, and a value stamped downstream would be a promise instead of an
   * observation.
   */
  saEffectiveConfig: z
    .object({
      digest: z.string().min(1),
      complete: z.boolean(),
      records: z
        .array(
          z.object({
            tool: z.string(),
            /**
             * Whether THIS tool's rules were established.
             *
             * Carried rather than re-derived downstream: the rule for what
             * counts as observed belongs to the observer, and two copies of
             * it would drift. `.default(false)` so a payload written before
             * this existed reads as undecided, which is the safe direction.
             */
            observed: z.boolean().default(false),
            files: z
              .array(
                z.object({
                  path: z.string(),
                  /**
                   * How this file was observed.
                   *
                   * Carried, not derived: `absent`, `unreadable` and
                   * `truncated` all have no hash, so dropping the state made
                   * them one indistinguishable record downstream and left
                   * nothing able to explain WHY a capture is partial.
                   */
                  state: z
                    .enum([
                      'hashed',
                      'absent',
                      'unreadable',
                      'truncated',
                      'references-unresolved',
                      'resolution-unsupported',
                      'internal',
                    ])
                    .default('hashed'),
                  sha256: z.string().nullable(),
                }),
              )
              .nullable(),
          }),
        )
        .default([]),
    })
    .optional(),
  /** The analyser that produced this scan, stamped by the producer itself. */
  saAnalyzerVersion: z.string().min(1).optional(),
});
